import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Download, ChevronDown, ChevronRight, Hash } from 'lucide-react';
import {
  API, PLATFORM_ICONS, int, tl, pct, short, div, toISO, trDate,
  Kpi, Section, Tabs, Loading, ErrorBox, DataTable,
} from './DynamicTracking';

// Yataş Grubu — Kampanya Kodları. Ajansın kampanya adlarına eklediği iş/brief kodlarını
// (`Eyl26-001`, `May26-004`, `Aug26-001` → Ağu26-001) kod bazında toplar. Kod kampanya
// KİMLİĞİNE göre son addan okunur (backend: routes/yatasCampaignCodes.js) — sonradan
// kod eklenen kampanyanın kodsuz günleri de aynı koda yazılır, Meta'da tek satıra iner.
// Ölçüldü (18 Eki 2026): kod yalnız Divanev (Google+Meta) ve Puffy (Google) hesaplarında var.

const MONTH_SHORT = ['', 'Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const PLATFORM_TR = { google: 'Google Ads', meta: 'Meta' };

function rangeOf(key) {
  const today = new Date();
  const y = new Date(today); y.setDate(today.getDate() - 1);
  switch (key) {
    case 'thisMonth':
      if (today.getDate() === 1) return rangeOf('lastMonth');
      return [toISO(new Date(today.getFullYear(), today.getMonth(), 1)), toISO(y)];
    case 'lastMonth':
      return [toISO(new Date(today.getFullYear(), today.getMonth() - 1, 1)), toISO(new Date(today.getFullYear(), today.getMonth(), 0))];
    case '90d': { const s = new Date(y); s.setDate(y.getDate() - 89); return [toISO(s), toISO(y)]; }
    case 'thisYear':
    default:
      return [toISO(new Date(today.getFullYear(), 0, 1)), toISO(y)];
  }
}
const PRESETS = [['thisYear', 'Bu yıl'], ['90d', 'Son 90 gün'], ['thisMonth', 'Bu ay'], ['lastMonth', 'Geçen ay']];
const PLATFORMS = [['all', 'Tümü'], ['google', 'Google Ads'], ['meta', 'Meta']];

// İş adlarını yazım farkından arındırarak birleştirir: "Kreatfiler/Kreatifler", "Kahve Makinesi /
// Kahve Makinesi Kampanyası - View Item" aynı iş. Anahtar = ünsüzler; biri diğerinin önekiyse birleşir.
// Dönen liste harcamaya göre sıralı, her grubun adı o gruptaki en çok harcayan yazım.
const KEY_DROP = /(view item|session start|kampanyas[ıi]|kampanya)/gi;
const labelKey = (l) => String(l).toLocaleLowerCase('tr').replace(KEY_DROP, '').replace(/[^a-zçğşüöı]/g, '').replace(/[aeıioöuü]/g, '');
function mergeLabels(campaigns) {
  const byLabel = new Map();
  for (const c of campaigns) byLabel.set(c.label, (byLabel.get(c.label) || 0) + c.cost);
  const groups = [];
  for (const [label, cost] of [...byLabel.entries()].sort((a, b) => b[1] - a[1])) {
    const k = labelKey(label);
    const g = groups.find((x) => k && (x.key.startsWith(k) || k.startsWith(x.key)));
    if (g) { g.cost += cost; g.all.push(label); } else groups.push({ key: k, label, cost, all: [label] });
  }
  return groups.sort((a, b) => b.cost - a.cost);
}

// Kampanya türü: Google `AdvertisingChannelType.VIDEO` → Video, Meta `OUTCOME_AWARENESS` → Awareness
const typeTr = (t) => {
  if (!t) return '';
  const v = String(t).replace(/^AdvertisingChannelType\./, '').replace(/^OUTCOME_/, '').replace(/_/g, ' ').toLowerCase();
  return v === 'performance max' ? 'PMax' : v.replace(/\b\w/g, (m) => m.toUpperCase());
};

const zero = () => ({ cost: 0, impressions: 0, clicks: 0, views: 0 });
const addTo = (t, c) => { t.cost += c.cost; t.impressions += c.impressions; t.clicks += c.clicks; t.views += c.views; };

function PlatformIcon({ p, className = 'h-4 w-4' }) {
  return <img src={PLATFORM_ICONS[p]} alt={PLATFORM_TR[p]} title={PLATFORM_TR[p]} className={`${className} shrink-0 object-contain`} />;
}

const csvCell = (v) => {
  const s = v == null ? '' : String(v);
  return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
function downloadCampaignCsv(name, codes) {
  const head = ['Kod', 'Mecra', 'Kampanya', 'Tür', 'Harcama', 'Gösterim', 'Tıklama', 'İzlenme', 'İlk gün', 'Son gün', 'Eski adlar'];
  const rows = codes.flatMap((k) => k.campaigns.map((c) => [
    k.code, PLATFORM_TR[c.platform], c.name, typeTr(c.type), c.cost, c.impressions, c.clicks, c.views, c.first, c.last,
    c.names.filter((x) => x !== c.name).join(' | '),
  ]));
  const body = rows.map((r) => r.map((v) => csvCell(typeof v === 'number' ? String(v).replace('.', ',') : v)).join(';'));
  const blob = new Blob([`﻿${[head.join(';'), ...body].join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = `${name}.csv`; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export default function CampaignCodes() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id;
  const [preset, setPreset] = useState('thisYear');
  const [range, setRange] = useState(() => rangeOf('thisYear'));
  const [platform, setPlatform] = useState('all');
  const [state, setState] = useState({ loading: true });
  const [refreshKey, setRefreshKey] = useState(0);
  const reqRef = useRef(0);

  const load = useCallback(() => {
    const id = ++reqRef.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetch(`${API}/api/yatas-campaign-codes?start=${range[0]}&end=${range[1]}`, { credentials: 'include', headers: { 'X-Brand-ID': brandId } })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        return j;
      })
      .then((j) => { if (id === reqRef.current) setState({ loading: false, data: j }); })
      .catch((e) => { if (id === reqRef.current) setState({ loading: false, error: e.message }); });
  }, [brandId, range]);

  useEffect(() => { if (brandId) load(); }, [brandId, range, refreshKey, load]);

  const pickPreset = (k) => { setPreset(k); setRange(rangeOf(k)); };
  const setCustom = (i, v) => {
    if (!v) return;
    setPreset('custom');
    setRange((r) => { const n = [...r]; n[i] = v; if (n[0] > n[1]) n[1 - i] = v; return n; });
  };

  // Mecra filtresi istemcide: kodlar kampanyalardan yeniden toplanır.
  const view = useMemo(() => {
    const d = state.data;
    if (!d) return null;
    const keep = (c) => platform === 'all' || c.platform === platform;
    const total = zero();
    const coded = zero();
    const codes = [];
    for (const k of d.codes) {
      const campaigns = k.campaigns.filter(keep);
      if (!campaigns.length) continue;
      const t = zero();
      let first = null; let last = null;
      for (const c of campaigns) {
        addTo(t, c); addTo(coded, c); addTo(total, c);
        if (!first || c.first < first) first = c.first;
        if (!last || c.last > last) last = c.last;
      }
      const labels = mergeLabels(campaigns);
      codes.push({ ...k, ...t, campaigns, first, last, labels, platforms: [...new Set(campaigns.map((c) => c.platform))] });
    }
    const uncoded = d.uncoded.filter(keep);
    for (const c of uncoded) addTo(total, c);
    codes.sort((a, b) => b.cost - a.cost);

    // Kod ayına göre (kodun kendi ayı, harcamanın düştüğü ay değil)
    const byMonth = new Map();
    for (const k of codes) {
      const key = `${k.year}-${String(k.month).padStart(2, '0')}`;
      const m = byMonth.get(key) || { key, label: `${MONTH_SHORT[k.month]}${String(k.year).slice(2)}`, google: 0, meta: 0, codes: 0 };
      for (const c of k.campaigns) m[c.platform] += c.cost;
      m.codes += 1;
      byMonth.set(key, m);
    }
    const months = [...byMonth.values()].sort((a, b) => a.key.localeCompare(b.key));
    return { total, coded, codes, uncoded, months, campaignCount: codes.reduce((s, k) => s + k.campaigns.length, 0) };
  }, [state.data, platform]);

  return (
    <div className="mx-auto max-w-[1500px] space-y-4 px-3 pb-16 pt-2 sm:px-5">
      <div className="sticky top-0 z-20 -mx-3 border-b border-gray-200/70 bg-white/90 px-3 py-3 backdrop-blur sm:-mx-5 sm:px-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Kampanya Kodları</h1>
            <p className="text-xs text-gray-500">{selectedBrand?.name} · {trDate(range[0])} – {trDate(range[1])}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Tabs value={platform} onChange={setPlatform} items={PLATFORMS} size="sm" />
            <Tabs value={preset} onChange={pickPreset} items={PRESETS} size="sm" />
            <div className="flex items-center gap-1 text-sm">
              <input type="date" value={range[0]} max={range[1]} onChange={(e) => setCustom(0, e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700" />
              <span className="text-gray-400">–</span>
              <input type="date" value={range[1]} min={range[0]} max={toISO(new Date())} onChange={(e) => setCustom(1, e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700" />
            </div>
            <button onClick={() => setRefreshKey((k) => k + 1)} title="Verileri yeniden çek"
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
              <RefreshCw className={`h-3.5 w-3.5 ${state.loading ? 'animate-spin' : ''}`} /> Yenile
            </button>
          </div>
        </div>
      </div>

      {state.error && <ErrorBox text={`Veri alınamadı: ${state.error}`} />}
      {state.loading && !view && <Loading h={240} />}

      {view && (
        <div className={`space-y-4 transition-opacity ${state.loading ? 'opacity-60' : ''}`}>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Kpi label="Kodlu harcama" value={tl(view.coded.cost)} />
            <Kpi label="Toplam içindeki pay" value={pct(div(view.coded.cost * 100, view.total.cost))}
              hint={`Dönemin Google + Meta harcaması: ${tl(view.total.cost)}`} />
            <Kpi label="Kod sayısı" value={int(view.codes.length)} />
            <Kpi label="Kodlu kampanya" value={int(view.campaignCount)} />
            <Kpi label="Kodlu gösterim" value={short(view.coded.impressions)} />
          </div>

          {view.codes.length === 0 ? (
            <Section title="Kod bazında harcama">
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <Hash className="h-6 w-6 text-gray-300" />
                <p className="text-sm text-gray-600">Bu dönemde adında kod (ör. Eyl26-001) geçen kampanya yok.</p>
                <p className="max-w-md text-xs text-gray-400">
                  Kod, kampanya adına ay + yıl + sıra numarası olarak eklenir. Adında yalnız ay etiketi (ör. Eyl26) olan kampanyalar aşağıda kodsuz listede görünür.
                </p>
              </div>
            </Section>
          ) : (
            <>
              <MonthBars months={view.months} />
              <CodeTable codes={view.codes} total={view.coded} csvName={`kampanya-kodlari-${selectedBrand?.name || ''}-${range[0]}_${range[1]}`} />
            </>
          )}

          <Section title="Kodsuz kampanyalar"
            subtitle={`Adında kod olmayan, dönemde harcaması olan kampanyalar · ${tl(view.total.cost - view.coded.cost)}`}>
            <DataTable
              rows={view.uncoded}
              searchKey={(r) => r.name}
              csvName={`kodsuz-kampanyalar-${range[0]}_${range[1]}`}
              initialSort={{ key: 'cost', dir: 'desc' }}
              columns={[
                {
                  key: 'name', label: 'Kampanya', value: (r) => r.name,
                  render: (r) => (
                    <span className="flex min-w-0 items-center gap-2">
                      <PlatformIcon p={r.platform} className="h-3.5 w-3.5" />
                      <span className="truncate" title={r.name}>{r.name}</span>
                    </span>
                  ),
                },
                { key: 'tag', label: 'Ay etiketi', value: (r) => r.monthTag || '—' },
                { key: 'cost', label: 'Harcama', value: (r) => r.cost, fmt: tl },
                { key: 'impressions', label: 'Gösterim', value: (r) => r.impressions, fmt: int },
                { key: 'clicks', label: 'Tıklama', value: (r) => r.clicks, fmt: int },
              ]}
            />
          </Section>

          <p className="px-1 text-[11px] leading-relaxed text-gray-400">
            Kod her kampanyanın en son adından okunur; kod sonradan eklendiyse kampanyanın seçili dönemdeki tüm harcaması o koda yazılır.
            “Aug” yazımı Ağu olarak birleştirilir. İzlenme: Google video izlenmesi (bumper'da gösterim), Meta ThruPlay.
          </p>
        </div>
      )}
    </div>
  );
}

function MonthBars({ months }) {
  const max = Math.max(...months.map((m) => m.google + m.meta), 1);
  return (
    <Section title="Kod ayına göre harcama" subtitle="Kodun taşıdığı ay (Eyl26-007 → Eyl26), harcamanın gerçekleştiği ay değil">
      <div className="space-y-2">
        {months.map((m) => {
          const t = m.google + m.meta;
          return (
            <div key={m.key} className="grid grid-cols-[56px_1fr_120px] items-center gap-3 text-xs">
              <span className="font-medium text-gray-700">{m.label}</span>
              <div className="flex h-5 overflow-hidden rounded-md bg-gray-100" title={`Google ${tl(m.google)} · Meta ${tl(m.meta)}`}>
                <div className="h-full bg-blue-500" style={{ width: `${(m.google / max) * 100}%` }} />
                <div className="h-full bg-indigo-300" style={{ width: `${(m.meta / max) * 100}%` }} />
              </div>
              <span className="text-right tabular-nums text-gray-700">{tl(t)} <span className="text-gray-400">· {m.codes} kod</span></span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex gap-4 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-blue-500" /> Google Ads</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-indigo-300" /> Meta</span>
      </div>
    </Section>
  );
}

const SORTS = {
  code: (k) => k.year * 10000 + k.month * 1000 + k.seq,
  cost: (k) => k.cost, impressions: (k) => k.impressions, clicks: (k) => k.clicks, views: (k) => k.views,
  ctr: (k) => div(k.clicks, k.impressions) ?? -1, campaigns: (k) => k.campaigns.length,
};

function CodeTable({ codes, total, csvName }) {
  const [open, setOpen] = useState(() => new Set());
  const [sort, setSort] = useState({ key: 'cost', dir: 'desc' });
  const rows = useMemo(() => {
    const f = SORTS[sort.key];
    return [...codes].sort((a, b) => (sort.dir === 'asc' ? f(a) - f(b) : f(b) - f(a)));
  }, [codes, sort]);
  const toggle = (code) => setOpen((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const th = (key, label, left) => (
    <th onClick={() => setSort((s) => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }))}
      className={`cursor-pointer select-none whitespace-nowrap px-3 py-2 font-medium hover:text-gray-800 ${left ? 'text-left' : 'text-right'}`}>
      {label}{sort.key === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
    </th>
  );

  return (
    <Section title="Kod bazında harcama" subtitle="Satıra tıklayınca koda bağlı kampanyalar açılır"
      right={(
        <div className="flex gap-2">
          <button onClick={() => setOpen(open.size ? new Set() : new Set(codes.map((k) => k.code)))}
            className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
            {open.size ? 'Hepsini kapat' : 'Hepsini aç'}
          </button>
          <button onClick={() => downloadCampaignCsv(csvName, codes)}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
        </div>
      )}>
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="w-full text-[13px]">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              {th('code', 'Kod', true)}
              <th className="px-3 py-2 text-left font-medium">İş</th>
              {th('campaigns', 'Kampanya')}
              {th('cost', 'Harcama')}
              <th className="px-3 py-2 text-right font-medium">Pay</th>
              {th('impressions', 'Gösterim')}
              {th('clicks', 'Tıklama')}
              {th('ctr', 'CTR')}
              {th('views', 'İzlenme')}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((k) => {
              const isOpen = open.has(k.code);
              return (
                <React.Fragment key={k.code}>
                  <tr onClick={() => toggle(k.code)} className="cursor-pointer hover:bg-blue-50/30">
                    <td className="whitespace-nowrap px-3 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
                        <span className="rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-[12px] font-semibold text-gray-800">{k.code}</span>
                        {k.rawSpellings.some((s) => s !== k.code) && (
                          <span className="text-[10px] text-gray-400" title="Kampanya adındaki yazım">({k.rawSpellings.filter((s) => s !== k.code).join(', ')})</span>
                        )}
                      </span>
                      <div className="ml-5 mt-0.5 text-[10px] tabular-nums text-gray-400">{trDate(k.first)} – {trDate(k.last)}</div>
                    </td>
                    <td className="max-w-[420px] px-3 py-2 text-gray-800">
                      <div className="flex items-center gap-2">
                        <span className="flex gap-1">{k.platforms.map((p) => <PlatformIcon key={p} p={p} className="h-3.5 w-3.5" />)}</span>
                        <span className="truncate" title={k.labels.flatMap((g) => g.all).join(' · ')}>{k.labels[0]?.label}</span>
                        {k.labels.length > 1 && (
                          <span className="shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                            title={`Aynı kod farklı işlerde: ${k.labels.map((g) => g.label).join(' · ')}`}>
                            +{k.labels.length - 1} iş
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-700">{int(k.campaigns.length)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums text-gray-900">{tl(k.cost)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-500">{pct(div(k.cost * 100, total.cost))}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-700">{int(k.impressions)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-700">{int(k.clicks)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-700">{pct(div(k.clicks * 100, k.impressions))}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-700">{k.views ? int(k.views) : '—'}</td>
                  </tr>
                  {isOpen && k.campaigns.map((c) => (
                    <tr key={`${c.platform}-${c.id}`} className="bg-gray-50/60 text-[12px]">
                      <td className="whitespace-nowrap px-3 py-1.5 pl-8 text-[10px] tabular-nums text-gray-400">{trDate(c.first)} – {trDate(c.last)}</td>
                      <td className="max-w-[340px] px-3 py-1.5 text-gray-700">
                        <div className="flex items-center gap-2">
                          <PlatformIcon p={c.platform} className="h-3.5 w-3.5" />
                          <span className="truncate" title={c.name}>{c.name}</span>
                          {c.type && <span className="shrink-0 rounded bg-white px-1.5 py-0.5 text-[10px] text-gray-500 ring-1 ring-gray-200">{typeTr(c.type)}</span>}
                        </div>
                        {(c.renamed || c.oldCodes.length > 0) && (
                          <div className="ml-5 mt-0.5 truncate text-[10px] text-gray-400"
                            title={c.names.filter((x) => x !== c.name).join('\n')}>
                            {c.oldCodes.length > 0 ? `Önceki kod: ${c.oldCodes.join(', ')} · ` : ''}
                            Eski ad: {c.names.filter((x) => x !== c.name).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-1.5" />
                      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-gray-800">{tl(c.cost)}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-gray-400">{pct(div(c.cost * 100, k.cost))}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-gray-600">{int(c.impressions)}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-gray-600">{int(c.clicks)}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-gray-600">{pct(div(c.clicks * 100, c.impressions))}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-gray-600">{c.views ? int(c.views) : '—'}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot className="border-t border-gray-200 bg-gray-50 text-[13px] font-semibold">
            <tr>
              <td className="px-3 py-2 text-gray-700" colSpan={2}>Toplam</td>
              <td className="px-3 py-2 text-right tabular-nums">{int(codes.reduce((s, k) => s + k.campaigns.length, 0))}</td>
              <td className="px-3 py-2 text-right tabular-nums">{tl(total.cost)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-500">%100</td>
              <td className="px-3 py-2 text-right tabular-nums">{int(total.impressions)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{int(total.clicks)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{pct(div(total.clicks * 100, total.impressions))}</td>
              <td className="px-3 py-2 text-right tabular-nums">{total.views ? int(total.views) : '—'}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Section>
  );
}
